import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CameraAltSharpIcon from "@mui/icons-material/CameraAltSharp";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { getDocumentsViaRecordId } from "../../../../Utils/apiServices";
import KenDialog from "../../../KenDialog";
import KenFilePreview from "../KenFilePreview";
import KenLoader from "../../../KenLoader";

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.KenColors.primary,
  },
  buttonBorder: {
    border: `1px dashed ${theme.palette.KenColors.primary}`,
    borderRadius: 6,
  },
  button: {
    color: theme.palette.KenColors.primary,
    textTransform: "none",
  },
  preview: {
    width: "100%",
    maxHeight: "60vh",
    objectFit: "contain",
    backgroundColor: "#000",
    borderRadius: 6,
  },
}));

const JPEG_QUALITY = 0.85;
const MAX_SIZE = 2000000;

export default function KenCameraUploadButton(props) {
  const { onCapture, recordId, fileName, applicationStateData } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);

  const [previewImage, setPreviewImage] = useState();
  const [filePreview, setFilePreview] = useState(false);
  const [loader, setLoader] = useState(false);

  const videoRef = useRef();
  const canvasRef = useRef();
  const streamRef = useRef();

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setError(null);
    setCapturedImage(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera is not supported on this browser.");
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user" } })
      .then((mediaStream) => {
        if (cancelled) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = mediaStream;
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(
            "Unable to access the camera. Please allow camera permission and try again."
          );
        }
      });

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open]);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setCapturedImage(null);
    setError(null);
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setCapturedImage(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
  };

  const handleRetake = () => setCapturedImage(null);

  const handleUsePhoto = () => {
    const canvas = canvasRef.current;
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return enqueueSnackbar("Could not process the captured photo. Please try again.", {
            variant: "error",
          });
        }
        if (blob.size > MAX_SIZE) {
          return enqueueSnackbar("Captured photo is too large. Please try capturing again.", {
            variant: "error",
          });
        }
        const file = new File([blob], `selfie-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        onCapture(file);
        handleClose();
      },
      "image/jpeg",
      JPEG_QUALITY
    );
  };

  const handlePreviewImage = () => {
    if (!recordId) return;
    setLoader(true);
    getDocumentsViaRecordId(recordId)
      .then((res) => {
        if (res?.success === true) {
          setPreviewImage(`data:image/jpeg;base64,${res?.data}`);
          setFilePreview(true);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoader(false));
  };

  const captureDialog = (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Capture Selfie
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={classes.preview}
              style={{ display: capturedImage ? "none" : "block" }}
            />
            {capturedImage && (
              <img src={capturedImage} alt="Captured selfie" className={classes.preview} />
            )}
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="secondary">
          Cancel
        </Button>
        {!error && (
          <>
            {capturedImage ? (
              <>
                <Button onClick={handleRetake} variant="secondary">
                  Retake
                </Button>
                <Button onClick={handleUsePhoto} variant="primary" color="primary">
                  Use Photo
                </Button>
              </>
            ) : (
              <Button onClick={handleCapture} variant="primary" color="primary">
                Capture
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );

  if (!recordId) {
    return (
      <>
        <div className={classes.buttonBorder}>
          <Button
            variant="secondary"
            onClick={handleOpen}
            startIcon={<CameraAltSharpIcon className={classes.icon} />}
            className={classes.button}
          >
            Open Camera & Capture
          </Button>
        </div>
        {captureDialog}
      </>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item lg={4} md={4} sm={12} xs={12}>
          {applicationStateData?.applicationData?.attachments?.map((status) => {
            if (status?.recordId == recordId) {
              return (
                <Typography
                  key={status.recordId}
                  style={{
                    color: "#ffffff",
                    fontSize: 14,
                    opacity: "0.6",
                    borderRadius: 6,
                    width: "100%",
                    background: status?.status === "Rejected" ? "red" : "#008000",
                    textTransform: "uppercase",
                    padding: "12px 0px",
                    cursor: "pointer",
                  }}
                >
                  {status?.status == null ? "Submitted" : status?.status}
                </Typography>
              );
            }
            return null;
          })}
        </Grid>
        <Grid item lg={4} md={4} sm={12} xs={12}>
          <Button
            variant="secondary"
            onClick={handlePreviewImage}
            style={{
              color: "#092682",
              border: "1px dashed #092682",
              borderRadius: 6,
              width: "100%",
            }}
          >
            Preview
          </Button>
        </Grid>
        <Grid item lg={4} md={4} sm={12} xs={12}>
          <Button
            variant="secondary"
            onClick={handleOpen}
            style={{
              color: "#092682",
              border: "1px dashed #092682",
              borderRadius: 6,
              width: "100%",
            }}
          >
            Retake
          </Button>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item md={12} textAlign="left">
          <Typography
            align="left"
            variant="subtitle2"
            sx={{ marginBottom: "-20px", marginTop: "8px", color: "#7A869A" }}
          >
            {fileName}
          </Typography>
        </Grid>
      </Grid>
      {loader ? (
        <KenLoader />
      ) : (
        <KenDialog open={filePreview} handleClose={() => setFilePreview(false)}>
          <KenFilePreview remove={false} width="50%" type="file" file={false} url={previewImage} />
        </KenDialog>
      )}
      {captureDialog}
    </Box>
  );
}
